import { useState } from 'react'
import { useFadeUp } from '../hooks/usefadeup'
import { FaPaperPlane, FaCheckCircle, FaUser, FaEnvelope, FaBriefcase, FaList, FaComments } from 'react-icons/fa'
import './Cotacoes.css'

const insuranceTypes = {
  empresa: {
    label: 'Empresa',
    options: [
      'Acidentes de Trabalho',
      'Frota Automóvel',
      'Seguro de Petróleo, Gás e Energia',
      'Seguro de Valores',
      'Recheio de Escritório',
      'Perda de Lucros',
      'Roubo',
      'Quebra de Vidros',
      'Cauções',
      'Mercadoria em Trânsito',
      'Todos os Riscos',
      'Danos Acidentais',
      'Responsabilidade Civil Geral',
      'Responsabilidade Civil Profissional',
      'Acidentes Pessoais',
      'Equipamento Eletrônico',
      'Seguro de Empreitada',
      'Máquinas Casco',
      'Avaria de Máquinas',
      'Seguro de Montagem',
      'Seguro de Saúde',
      'Plano de Saúde em Grupo'
    ]
  },
  particulares: {
    label: 'Particulares',
    options: [
      'Automóvel',
      'Roubo',
      'Todos os Riscos',
      'Seguro de Casa',
      'Recheio de Casa',
      'Equipamento Eletrônico',
      'Seguro de Vida',
      'Seguro de Saúde',
      'Acidentes de Trabalhos Domésticos'
    ]
  }
}

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'

export default function Cotacoes() {
  const ref = useFadeUp<HTMLElement>()
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    insuranceType: 'empresa',
    insuranceOption: '',
    message: ''
  })
  const [submitted, setSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'insuranceType' && { insuranceOption: '' })
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.fullName || !formData.email || !formData.insuranceOption) return

    setIsLoading(true)
    setError('')

    try {
      const response = await fetch(`${API_URL}/api/cotacao`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          insuranceType: insuranceTypes[formData.insuranceType as keyof typeof insuranceTypes].label,
          insuranceOption: formData.insuranceOption,
          message: formData.message
        })
      })

      if (response.ok) {
        setSubmitted(true)
        setTimeout(() => {
          setSubmitted(false)
          setFormData({
            fullName: '',
            email: '',
            insuranceType: 'empresa',
            insuranceOption: '',
            message: ''
          })
        }, 4000)
      } else {
        const data = await response.json()
        setError(data?.error || data?.errors?.[0]?.message || 'Erro ao enviar. Tente novamente.')
      }
    } catch {
      setError('Erro de ligação. Verifique a sua conexão e tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  const currentOptions = insuranceTypes[formData.insuranceType as keyof typeof insuranceTypes].options

  return (
    <section id="cotacoes" className="cot" ref={ref}>
      <div className="cot-header" data-fade data-fade-delay="0">
        <h2 className="cot-title">Solicitar Cotação</h2>
        <p className="cot-subtitle">Preencha o formulário abaixo e receba uma proposta personalizada em breve</p>
      </div>

      <div className="cot-container">
        <div className="cot-layout">
          <div className="cot-image-wrapper" data-fade data-fade-delay="60">
            <img src="/insurance-quote.png" alt="Consultoria de seguros" className="cot-image" />
            <div className="cot-image-overlay">
              <div className="cot-overlay-content">
                <h3>Proteção à sua medida</h3>
                <p>Garantimos a segurança e tranquilidade que você, sua família e sua empresa merecem, com as melhores soluções do mercado.</p>
              </div>
            </div>
          </div>

          <div className="cot-form-wrapper" data-fade data-fade-delay="120">
            {!submitted ? (
            <form className="cot-form" onSubmit={handleSubmit}>
              <div className="cot-form-group">
                <label htmlFor="fullName" className="cot-label">Nome Completo *</label>
                <div className="cot-input-wrapper">
                  <FaUser className="cot-input-icon" />
                  <input
                    id="fullName"
                    type="text"
                    name="fullName"
                    className="cot-input"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="João Silva"
                    required
                  />
                </div>
              </div>

              <div className="cot-form-group">
                <label htmlFor="email" className="cot-label">Email *</label>
                <div className="cot-input-wrapper">
                  <FaEnvelope className="cot-input-icon" />
                  <input
                    id="email"
                    type="email"
                    name="email"
                    className="cot-input"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="joao@exemplo.com"
                    required
                  />
                </div>
              </div>

              <div className="cot-form-row">
                <div className="cot-form-group">
                  <label htmlFor="insuranceType" className="cot-label">Tipo de Seguro *</label>
                  <div className="cot-input-wrapper">
                    <FaBriefcase className="cot-input-icon" />
                    <select
                      id="insuranceType"
                      name="insuranceType"
                      className="cot-select"
                      value={formData.insuranceType}
                      onChange={handleInputChange}
                    >
                      {Object.entries(insuranceTypes).map(([key, val]) => (
                        <option key={key} value={key}>
                          {val.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="cot-form-group">
                  <label htmlFor="insuranceOption" className="cot-label">Opção de Seguro *</label>
                  <div className="cot-input-wrapper">
                    <FaList className="cot-input-icon" />
                    <select
                      id="insuranceOption"
                      name="insuranceOption"
                      className="cot-select"
                      value={formData.insuranceOption}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Selecione uma opção</option>
                      {currentOptions.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="cot-form-group">
                <label htmlFor="message" className="cot-label">Mensagem Adicional</label>
                <div className="cot-input-wrapper">
                  <FaComments className="cot-input-icon textarea-icon" />
                  <textarea
                    id="message"
                    name="message"
                    className="cot-textarea"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Conte-nos mais sobre suas necessidades..."
                    rows={4}
                  />
                </div>
              </div>

              {error && (
                <p className="cot-error">{error}</p>
              )}

              <button type="submit" className="cot-button" disabled={isLoading}>
                <span>{isLoading ? 'A enviar...' : 'Solicitar Cotação'}</span>
                <FaPaperPlane style={{ marginLeft: '8px' }} />
              </button>

              <p className="cot-disclaimer">* Campos obrigatórios</p>
            </form>
          ) : (
            <div className="cot-success">
              <div className="cot-success-icon">
                <FaCheckCircle />
              </div>
              <h3 className="cot-success-title">Cotação Solicitada com Sucesso!</h3>
              <p className="cot-success-message">
                Obrigado, {formData.fullName}. Entraremos em contato em breve no seu email.
              </p>
            </div>
          )}
        </div>
        </div>
      </div>
    </section>
  )
}